import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Download, ExternalLink, RefreshCw, AlertTriangle } from 'lucide-react';

interface ReportRow {
  email: string;
  firstName: string;
  lastName: string;
  countyState: string;
  listid: number;
  subscribedate: string;
  unsubscribedate: string;
  status: 'active' | 'unsubscribed' | 'bounced';
}

interface ReportResponse {
  rows: ReportRow[];
  hasMore: boolean;
  title: string;
  bucket: string;
  domain: string | null;
  segmentId: number | null;
  segmentName: string | null;
}

const PAGE_SIZE = 100;

const STATUS_CLASS: Record<ReportRow['status'], string> = {
  active: 'text-green-700 dark:text-green-400',
  unsubscribed: 'text-amber-700 dark:text-amber-400',
  bounced: 'text-red-700 dark:text-red-400',
};

export default function DomainReport() {
  const [searchParams] = useSearchParams();
  const bucket = searchParams.get('bucket') || '';
  const domain = searchParams.get('domain') || '';
  const segmentId = searchParams.get('segmentId') || '';

  const [rows, setRows] = useState<ReportRow[]>([]);
  const [title, setTitle] = useState('Contact report');
  const [segmentName, setSegmentName] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buildParams = useCallback(
    (nextCursor = '') => {
      const p = new URLSearchParams({
        bucket,
        limit: String(PAGE_SIZE),
        cursor: nextCursor,
      });
      if (domain) p.set('domain', domain);
      if (segmentId) p.set('segmentId', segmentId);
      return p;
    },
    [bucket, domain, segmentId]
  );

  const loadPage = useCallback(
    async (reset: boolean, nextCursor = '') => {
      if (!bucket) {
        setError('Missing report bucket.');
        setLoading(false);
        return;
      }
      if (reset) {
        setLoading(true);
        setRows([]);
        setCursor('');
        setError(null);
      } else {
        setLoadingMore(true);
      }
      try {
        const res = await axios.get<ReportResponse>(
          `/api/interspire/domain-contacts/report?${buildParams(nextCursor)}`,
          { timeout: 120_000 }
        );
        setTitle(res.data.title);
        setSegmentName(res.data.segmentName);
        setHasMore(res.data.hasMore);
        setRows((prev) => (reset ? res.data.rows : [...prev, ...res.data.rows]));
        if (res.data.rows.length > 0) {
          setCursor(res.data.rows[res.data.rows.length - 1].email);
        }
      } catch (e: any) {
        setError(e?.response?.data?.error ?? e?.message ?? 'Failed to load report');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [bucket, buildParams]
  );

  useEffect(() => {
    loadPage(true);
  }, [loadPage]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const p = buildParams('');
      p.delete('limit');
      p.delete('cursor');
      const res = await axios.get(`/api/interspire/domain-contacts/report/export?${p}`, {
        responseType: 'blob',
        timeout: 0,
      });
      const safeDomain = (domain || 'segment').replace(/[^a-z0-9._-]/gi, '_');
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `domain_report_${safeDomain}_${bucket}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch {
      window.alert('CSV export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const backHref = segmentId
    ? `/domain-lookup?segmentId=${encodeURIComponent(segmentId)}`
    : '/domain-lookup';

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to={backHref}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-200 mb-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Domain Lookup
          </Link>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h1>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
            {domain ? (
              <>
                Domain <span className="font-medium text-gray-800 dark:text-slate-200">@{domain.replace(/^@/, '')}</span>
              </>
            ) : (
              'All segment members (no domain filter)'
            )}
            {segmentName && (
              <>
                {' · '}
                Segment <span className="font-medium text-gray-800 dark:text-slate-200">{segmentName}</span>
                {segmentId ? ` (ID ${segmentId})` : ''}
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => loadPage(true)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || loading || (!!error && rows.length === 0)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {exporting ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700 flex justify-between text-sm text-gray-500 dark:text-slate-400">
          <span>
            {loading
              ? 'Loading…'
              : `${rows.length.toLocaleString()} contact${rows.length !== 1 ? 's' : ''} loaded${
                  hasMore ? '+' : ''
                }`}
          </span>
          <span className="text-xs">
            Unsubscribe date shown when Interspire stored a real timestamp (not a 0/1 flag).
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-slate-700/50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Email</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">First</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Last</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">County, State</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">List</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Subscribed</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Unsubscribed</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
              {!loading && rows.length === 0 && !error && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                    No contacts in this bucket.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={`${r.listid}-${r.email}`} className="hover:bg-gray-50 dark:hover:bg-slate-700/40">
                  <td className="px-3 py-2 font-medium text-gray-900 dark:text-slate-100 max-w-[220px] truncate" title={r.email}>
                    {r.email}
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-slate-300">{r.firstName || '—'}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-slate-300">{r.lastName || '—'}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-slate-300">{r.countyState || '—'}</td>
                  <td className="px-3 py-2 text-right text-gray-500 dark:text-slate-400">#{r.listid}</td>
                  <td className="px-3 py-2 text-gray-500 dark:text-slate-400 whitespace-nowrap">
                    {r.subscribedate || '—'}
                  </td>
                  <td className="px-3 py-2 text-amber-700 dark:text-amber-400 whitespace-nowrap">
                    {r.unsubscribedate || (r.status === 'unsubscribed' ? 'Unknown' : '—')}
                  </td>
                  <td className={`px-3 py-2 font-medium capitalize ${STATUS_CLASS[r.status]}`}>
                    {r.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {hasMore && (
          <div className="px-4 py-3 border-t border-gray-100 dark:border-slate-700">
            <button
              type="button"
              onClick={() => loadPage(false, cursor)}
              disabled={loadingMore}
              className="text-sm text-blue-600 hover:underline disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {loadingMore ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
              Load more
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
